/*
 * Confidential and Proprietary.
 * Do not distribute without Bloom Harbor Gifts consent.
 * Copyright Bloom Harbor Gifts 2019. All rights reserved.
 */

import React, { useEffect, useState } from 'react';
import mbpLogger from '@module-logger/mbp';
import { Grid } from '@material-ui/core';
import { useSelector } from 'react-redux';
import { useQuery } from '@apollo/client';
import {
    arrayOf, bool, func, object, shape, string,
} from 'prop-types';
import { Link } from 'react-router-dom';
import { makeStyles } from '@material-ui/styles';

import { GRAPHQL_ENV } from '../../../../gql';
import findCategoryProducts from '../gql/findCategoryProducts';
import SimpleProduct from '../../GraphqlCategoryPage/Partials/GraphqlProductContainer/Partials/GraphqlSimpleProduct/GraphqlSimpleProduct';
import LoadMoreButton from '../../GraphqlCategoryPage/Partials/GraphqlProductContainer/Partials/LoadMoreButton';
import { RenderCards } from '../Loader/GenericTemplateLoader';
import { getSSRDeviceType } from '../../../../../state/ducks/App/App-Selectors';
import useExperimentServiceAttributes from '../../../../helpers/experimentService/useExperimentServiceAttributes';
import useVertexAIProductImpression from '../../../../helpers/hooks/useVertexAIProductImpression';

const useStyles = makeStyles((theme) => ({
    container: {
        width: '1440px',
        '& h1': {
            textAlign: 'center',
            fontSize: '1.5rem',
            fontFamily: 'ivypresto-text, PlayfairDisplayBold, serif',
            [theme.breakpoints.down(600)]: {
                fontSize: '1.3rem',
            },
        },
    },
    productRow: {
        boxSizing: 'border-box',
        width: 'auto',
    },
    simpleProduct: {
        flex: '0 0 50%',
        maxWidth: '50%',
        marginBottom: '30px',
        position: 'relative',
        width: '100%',
        paddingRight: '8px',
        paddingLeft: '8px',
        [theme.breakpoints.up(600)]: {
            paddingRight: '10px',
            paddingLeft: '10px',
            flex: '0 0 25%',
            maxWidth: '25%',
        },
    },
    dummySimpleProduct: {
        display: 'none',
        [theme.breakpoints.up(599)]: {
            display: 'block',
        },
        [theme.breakpoints.up(769)]: {
            display: 'none',
        },
    },
    eventCancel: {
        pointerEvents: 'none !important',
        '& a': {
            pointerEvents: 'none !important',
        },
    },
}));

const MoreSubscriptionsTemplate = ({
    item, brandName, templateData, handleTrackEvent,
}) => {
    if (typeof window === 'undefined') return null;
    const classes = useStyles();
    const ssrDeviceType = useSelector(getSSRDeviceType);
    const totalPages = 50;
    const collection = item?.more_subscriptions_block;
    const [pageCount, setPageCount] = useState(1);
    const [loadingBtn, setLoadingBtn] = useState(false);
    const { targeting, context, isGraphqlTargetingEnabled } = useExperimentServiceAttributes({ queryName: 'findCategoryById' });

    const CATEGORY_PRODUCT_QUERY = findCategoryProducts(isGraphqlTargetingEnabled, context?.api === 'experiments');

    const {
        loading, error, data, fetchMore,
    } = useQuery(CATEGORY_PRODUCT_QUERY, {
        variables: {
            productOptions: {
                locationType: 'Residence', modelName: '', orderBy: 'BEST_SELLERS', page: pageCount, pageSize: 4,
            },
            brand: brandName,
            environment: GRAPHQL_ENV,
            id: collection.collection_identifier,
            locale: 'en-us',
            zipCode: '',
            locationType: 'Residence',
            ...(isGraphqlTargetingEnabled ? { targeting } : {}),
        },
        context,
    });

    useVertexAIProductImpression({
        products: data?.findCategoryById?.products,
        experiments: data?.findCategoryById?.experiments,
        attributionToken: data?.findCategoryById?.attributionToken,
        pageType: 'category',
        category: { id: collection.collection_identifier, name: collection.collection_identifier },
    });

    useEffect(() => {
        setLoadingBtn(false);
    }, [data?.findCategoryById?.products?.length]);

    if (loading && !data && !error) {
        return <RenderCards />;
    }

    if (error) {
        mbpLogger.logError({
            appName: process.env.npm_package_name,
            query: CATEGORY_PRODUCT_QUERY,
            component: 'MoreSubscriptionsTemplate.js',
            message: 'Error loading CMS data for more subscriptions page',
            env: GRAPHQL_ENV,
            error,
        });
        return null;
    }

    const subscriptionData = data?.findCategoryById || {};
    const isLoadMoreAvailable = subscriptionData?.totalProducts > subscriptionData?.products?.length;

    const handleLoadMore = () => {
        if (typeof fetchMore === 'function') {
            fetchMore({
                variables: {
                    productOptions: {
                        locationType: 'Residence', modelName: '', orderBy: 'BEST_SELLERS', page: pageCount, pageSize: 8,
                    },
                    brand: brandName,
                    environment: GRAPHQL_ENV,
                    id: collection.collection_identifier,
                    locale: 'en-us',
                    zipCode: '',
                    locationType: 'Residence',
                },
            });
            setPageCount((prev) => prev + 1);
        }
    };

    const handleProductClick = (product) => {
        handleTrackEvent('Subscriptions Experience', 'Choose this Plan from Collection', product?.partNumber || '');
    };

    return (
        <div className={classes.container}>
            <h1>{collection.title}</h1>
            <Grid container className={classes.productRow}>
                {subscriptionData?.products?.length !== 0 ? subscriptionData?.products?.map((card, i) => {
                    const {
                        name,
                        brandId: productBrandId,
                        partNumber,
                        image,
                        skuPriceRange,
                        availability,
                        prices,
                        isPassportEligible,
                        productSkus,
                        reviews,
                        showShopName = false,
                    } = card;
                    return (
                        <Grid item key={`${name}-${partNumber}`} className={classes.simpleProduct}>
                            {/* for the product not available flow we need to know whether the simple product component was rendered on a category page vs the homepage or any other location */}
                            {/* to do this we check whether the product container got categoryData as opposed to some other kind of graphql data */}
                            <Link
                                to={{
                                    search: '',
                                    hash: '',
                                    state: { selectedProduct: card, templateData },
                                    pathname: '/subscriptions/customizable-plan/choose-item',
                                }}
                            >
                                <div aria-label={name} tabIndex={0} role="button" onClick={() => handleProductClick(card)} onKeyDown={() => handleProductClick(card)}>
                                    <div className={classes.eventCancel}>
                                        <SimpleProduct
                                            quickview={false} // disabled for products that are personalizable / CYO
                                            partNumber={partNumber}
                                            name={name}
                                            prices={prices}
                                            image={image}
                                            url={window.location.pathname}
                                            skuPriceRange={skuPriceRange}
                                            availability={availability}
                                            fromCategoryPage={typeof categoryData === 'object'}
                                            productReviews={reviews}
                                            categoryName={collection.title || ''}
                                            categoryId={collection.collection_identifier || '18F-Subscriptions-Flowers-1027'}
                                            categoryLegacyId={null}
                                            skipAddons={false}
                                            showMovieUpsell={false}
                                            track
                                            trackEvent={() => {}}
                                            featureFlags={{}}
                                            presentation_family=""
                                            isPassportEligible={isPassportEligible}
                                            shouldRequestEarliestDeliveryDate={i < 12}
                                            productSkus={productSkus}
                                            filterOptions={{}}
                                            productBrand={productBrandId}
                                            categoryData={{}}
                                            positionInCategory={i}
                                            showSimpleProductRedesignAbTest
                                            userToken=""
                                            sisterBrandProps={{}}
                                            snipeData={{}}
                                            showShopName={showShopName}
                                        />
                                    </div>
                                </div>
                            </Link>
                        </Grid>
                    );
                }) : null}
            </Grid>
            {collection?.enable_button && (
                <LoadMoreButton
                    hasMore={(pageCount <= totalPages)}
                    isLoadMoreAvailable={isLoadMoreAvailable}
                    ssrDeviceType={ssrDeviceType}
                    onLoadMore={handleLoadMore}
                    productToLoad={24}
                    loading={loadingBtn}
                    setLoading={setLoadingBtn}
                    totalProducts={subscriptionData?.totalProducts}
                    tracking={{
                        eventCategory: 'Subscriptions Experience',
                        eventAction: 'Load More',
                        eventLabel: 'Show More Subscriptons',
                    }}
                />
            )}
        </div>
    );
};

MoreSubscriptionsTemplate.propTypes = {
    item: object.isRequired,
    brandName: string.isRequired,
    templateData: shape({
        seo: shape({
            breadcrumb: arrayOf(
                shape({
                    title: string.isRequired,
                    href: string.isRequired,
                }).isRequired,
            ),
            keywords: string,
            meta_description: string,
            seo_searchable: bool.isRequired,
            page_title: string,
        }).isRequired,
        category_blocks: arrayOf(
            shape({
                main_banner: shape({
                    link: shape({
                        href: string.isRequired,
                        title: string.isRequired,
                    }),
                    desktop_image: shape({
                        url: string.isRequired,
                    }),
                }),
                sub_banner: shape({
                    desktop_image: shape({
                        url: string.isRequired,
                        title: string.isRequired,
                    }),
                }),
                bottom_banners: shape({
                    title: string.isRequired,
                    banners: arrayOf({
                        link: shape({
                            href: string.isRequired,
                        }),
                        desktop_image: shape({
                            url: string.isRequired,
                            title: string.isRequired,
                        }),
                    }),
                }),
            }),
        ),
    }).isRequired,
    handleTrackEvent: func.isRequired,
};

export default MoreSubscriptionsTemplate;
